/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Moderation admin section.
 *
 * Single-page container with internal sub-tabs (Bans / IP Bans / Watchlist /
 * Warnings / Appeals / Servers / Config / Stats / Detection Feed). Each
 * sub-tab lives in its own component for clarity — see `./moderation/`.
 *
 * The container also fetches the stats panel once so we can show
 * pending-appeal / pending-ban badges in the tab strip.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import {
  Ban, Globe, Eye, AlertTriangle, MessageSquare,
  Server, Settings, BarChart3, Radio,
} from "lucide-react";

import BansTab from "./moderation/BansTab";
import IpBansTab from "./moderation/IpBansTab";
import WatchlistTab from "./moderation/WatchlistTab";
import WarningsTab from "./moderation/WarningsTab";
import AppealsTab from "./moderation/AppealsTab";
import ServersTab from "./moderation/ServersTab";
import ConfigTab from "./moderation/ConfigTab";
import StatsTab from "./moderation/StatsTab";
import FeedTab from "./moderation/FeedTab";

const TABS = [
  { id: "stats",     label: "Stats",          icon: BarChart3,    Component: StatsTab },
  { id: "bans",      label: "Bans",           icon: Ban,          Component: BansTab },
  { id: "ipbans",    label: "IP Bans",        icon: Globe,        Component: IpBansTab },
  { id: "watchlist", label: "Watchlist",      icon: Eye,          Component: WatchlistTab },
  { id: "warnings",  label: "Warnings",       icon: AlertTriangle, Component: WarningsTab },
  { id: "appeals",   label: "Appeals",        icon: MessageSquare, Component: AppealsTab },
  { id: "servers",   label: "Servers",        icon: Server,       Component: ServersTab },
  { id: "config",    label: "Config",         icon: Settings,     Component: ConfigTab },
  { id: "feed",      label: "Detection Feed", icon: Radio,        Component: FeedTab },
];

export function Moderation() {
  const toast = useToast();
  const [active, setActive] = useState("stats");
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/admin/moderation/stats")
    .then(({ data }) => setStats(data))
    .catch((e) => toast.error(apiError(e)));

  useEffect(() => { loadStats(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ActiveComponent = TABS.find((t) => t.id === active)?.Component || StatsTab;
  const pendingAppeals = stats?.pending_appeals || 0;

  return (
    <div className="space-y-4 fade-up" data-testid="moderation-root">
      {/* Sub-tab strip */}
      <nav className="glass-card p-2 flex gap-1 overflow-x-auto" data-testid="moderation-tabs">
        {TABS.map((t) => {
          const isActive = active === t.id;
          const badge = t.id === "appeals" && pendingAppeals > 0 ? pendingAppeals : null;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              data-testid={`moderation-tab-${t.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-[rgba(0,229,255,0.1)] text-[var(--teal)]"
                  : "text-[var(--muted)] hover:text-[var(--bone)]"
              }`}
            >
              <t.icon size={14} /> {t.label}
              {badge != null && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-500/30 text-rose-200 text-[10px] font-mono">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <ActiveComponent toast={toast} stats={stats} reloadStats={loadStats} />
    </div>
  );
}
